<script setup>
import 'core-js/es/promise/all-settled';
import { onBeforeMount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

// // Utils imports
import identifiers from '@/utils/identifiers';
import backendApis from '@/utils/backendApis';
import { logDebug, logError, logInfo } from '@/utils/logger';

// // Store management imports
import { useBreakStore } from '@/stores/break';
import { useDirectionStore } from '@/stores/direction';
import { useGeneralStore } from '@/stores/general';
import { useTrackerStore } from '@/stores/tracker';

// // Utils imports
import {
  initializeMetrics,
  sendOverlayStarted,
  sendOverlayShown,
  sendOverlayError,
  OVERLAY_ERROR,
} from '@/utils/nacxMetrics';

// // Utils imports
import templateTrees from '@/utils/templateTrees';
import dataAnalyzers, { BreakModeEnum } from '@/utils/dataAnalyzers';
import { filterValidHotspots } from '@/utils/hotspotValidation';
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';

// // View imports
import Home from '@/views/Home/HomeView.vue';
import SamsungShopping from '@/views/SamsungShopping/SamsungShoppingView.vue';
// import SamsungGame from '@/views/SamsungGame/SamsungGameView.vue';
// import SamsungDelivery from '@/views/SamsungDelivery/SamsungDeliveryView.vue';
import FourthWall from '@/views/FourthWall/FourthWallView.vue';
import TvPlusLinker from '@/views/TvPlusLinker/TvPlusLinkerView.vue';
// import { waitForFonts } from './utils/waitForFonts';

const { isIframe, isVisible, isBreakInteracted, isTizen, trackerDuid, trackingInfo } =
  storeToRefs(useTrackerStore());

const storeBreak = useBreakStore();
const {
  isBreakReady,
  isBreakBackgroundFree,
  isBreakBackgroundAnImage,
  breakGeneratedIdentifier,
  breakType,
  breakTemplate,
  breakTemplateUuid,
  breakUuid,
  breakTitle,
  breakSponsorName,
  breakPlaybackVideoSrc,
  breakPlaybackVideoDuration,
  breakBackgroundImageSrc,
  breakBackgroundColor,
  breakHeadlineCopy,
  breakHeadlineColor,
  breakTaglineCopy,
  breakTaglineColor,
  breakScreenPlacement,
  breakCorrectOption,
  breakTriviaTidbit,
  breakAiTerm,
  breakAiType,
  breakAiCriteria,
  breakSponsorLogos,
  breakProducts,
  breakOptions,
  breakHotspots,
  breakCategories,
  breakAiRecommendations,
  breakAiRecommendationsStatus,
  breakAiRecommendationsErrorCode,
  breakDisplayMode,
  breakSearchParameters,
} = storeToRefs(useBreakStore());
const storeDirection = useDirectionStore();
const { directionGroup, directionModule } = storeToRefs(useDirectionStore());
const storeGeneral = useGeneralStore();
const storeTracker = useTrackerStore();

const groupDirectory = {
  fourthWall: {
    group: 'FourthWall',
  },
  basicOverlays: {
    group: 'BasicOverlays',
  },
  transparentOverlays: {
    group: 'TransparentOverlays',
  },
  simplePolls: {
    group: 'SimplePolls',
  },
  multiStageOverlays: {
    group: 'MultiStageOverlays',
  },
  heroPolls: {
    group: 'HeroPolls',
  },
  samsungGame: {
    group: 'SamsungGame',
  },
  samsungDelivery: {
    group: 'SamsungDelivery',
  },
  samsungTvPlus: {
    group: 'SamsungTvPlus',
  },
  tst: {
    group: 'Test',
  },
};

const isUnableToPopulate = ref(false);
const appInstance = import.meta.env.VITE_APP_ENV;
const catchErrorPrefix = import.meta.env.VITE_APP_CATCH_ERROR_PREFIX;

fetch(`${catchErrorPrefix}${appInstance}/appReachable`)
  .then((response) => {
    if (response.ok) {
      logInfo('App is reachable');
    } else {
      logInfo('App is not reachable');
    }
  })
  .catch((errorResponse) => {
    logInfo('App has an error:', errorResponse);
  });

// // App functions

const setDirection = (typeParam, templateParam) => {
  directionGroup.value = templateTrees[typeParam][templateParam].group;
  directionModule.value = templateTrees[typeParam][templateParam].module;
};

const callDirectionGroup = () => {
  return directionGroup.value;
};

const callInabilityToPopulate = () => {
  return isUnableToPopulate;
};

const initializer = async () => {
  // // Set up tracking first
  storeTracker.blankTracker();
  storeBreak.blankBreak();

  // // Define query params and set up display mode if provided
  breakSearchParameters.value = dataAnalyzers.separateQueryStringParameters(
    storeGeneral.instance.searchString,
  );
  breakDisplayMode.value = await dataAnalyzers.getDisplayMode(breakSearchParameters.value);
  logInfo(`Break display mode: `, breakDisplayMode.value);
  // breakDisplayMode.value = BreakModeEnum.FULLMODE;

  // // load the rest
  // // Get the isTizen variable
  isTizen.value = CommunicationUtil.isTizen();
  isIframe.value = window.self !== window.top;
  // // Call the nasbUiStart & creativeView beacons first before attempting to
  // // load the rest
  fetch(`${catchErrorPrefix}${appInstance}/appReachable?is_tizen=${isTizen.value}`);

  // // Only make beacon calls if not in full mode or browser mode (browser iframe)
  const isFullMode = breakDisplayMode.value === BreakModeEnum.FULLMODE;
  const isBrowserMode = breakDisplayMode.value === BreakModeEnum.BROWSER;
  const shouldSkipBeaconCalls = ref(isFullMode || isBrowserMode);
  logInfo('Should skip beacon calls: ', shouldSkipBeaconCalls.value);

  if (!shouldSkipBeaconCalls.value && isTizen.value && !CommunicationUtil.isContentOverlay()) {
    trackingInfo.value = await CommunicationUtil.getTrackingInfo();
    if (
      trackingInfo &&
      trackingInfo.value.trackingEvents &&
      trackingInfo.value.trackingEvents.length > 0
    ) {
      storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
        trackingInfo.value,
        'nasbUiStart',
        null,
      );
      storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
        trackingInfo.value,
        'creativeView',
        null,
      );
    }
  } else {
    console.info(
      `Skipping trackers - isTizen: ${isTizen.value} & isOverlay: ${CommunicationUtil.isContentOverlay()} & shouldSkipBeaconCalls: ${shouldSkipBeaconCalls.value}`,
    );
  }

  if (isTizen.value) {
    trackerDuid.value = await CommunicationUtil.getDuid();
  }

  // // DO IT IN THE BUILD PROCESS (await for TemplateReqs)

  storeDirection.blankDirection();

  // // Process the data
  await dataAnalyzers
    // .loadLocalJsonData()
    .loadUrlJsonData(breakSearchParameters.value)
    // .loadQueryStringJsonData(storeGeneral.instance.searchString)
    .then(async (capturedJson) => {
      breakGeneratedIdentifier.value = identifiers.generateV4Uuid();
      logInfo(`CAPTURED JSON: `, capturedJson);

      if (capturedJson.template_family) {
        if (capturedJson.template_family === 'tv_overlay_break') {
          breakType.value = 'tvOverlayBreak';
        } else if (capturedJson.template_family === 'tv_game_break') {
          breakType.value = 'tvGameBreak';
        } else if (capturedJson.template_family === 'tv_delivery_break') {
          breakType.value = 'tvDeliveryBreak';
        } else if (capturedJson.template_family === 'tv_deep_link') {
          breakType.value = 'tvDeepLink';
        } else if (capturedJson.template_family === 'tv_overlay_stream') {
          breakType.value = 'tvOverlayStream';
        } else {
          breakType.value = 'tvOverlayBreak';
        }
      } else {
        breakType.value = 'tvOverlayBreak';
        // breakType.value = 'tvOverlayStream';
      }

      logDebug('TODO: Use data.template_uuid instead of data.template');
      breakTemplateUuid.value = capturedJson.template_uuid
        ? capturedJson.template_uuid.toString()
        : '';
      if (capturedJson.template) {
        breakTemplate.value = capturedJson.template.toString();
      } else {
        breakTemplate.value = '1';
      }

      setDirection(breakType.value, breakTemplate.value);

      logDebug(`DEBUG: breakType: ${breakType.value}, breakTemplate: ${breakTemplate.value}`);
      logDebug(`DEBUG: directionGroup after setDirection: ${directionGroup.value}`);
      logDebug(`DEBUG: directionModule after setDirection: ${directionModule.value}`);

      breakUuid.value = capturedJson?.uuid;
      breakTitle.value = capturedJson?.title;

      // // NACX Metrics — only tvOverlayStream templates consume this; others use VAST beacons.
      if (breakType.value === 'tvOverlayStream') {
        initializeMetrics();
        sendOverlayStarted();
      }

      if (capturedJson.sponsor) {
        let capturedSponsorLogosList = [];

        breakSponsorName.value = capturedJson.sponsor.sponsor_name;

        if (capturedJson.sponsor.sponsor_logo) {
          capturedSponsorLogosList.push(capturedJson.sponsor.sponsor_logo);
        } else {
          capturedSponsorLogosList = capturedJson.sponsor.sponsor_logos;
        }

        breakSponsorLogos.value = capturedSponsorLogosList.map((item) => {
          return {
            sponsorLogoImageSrc: item.url,
            // sponsorLogoImageWidth: item.width,
            // sponsorLogoImageHeight: item.height,
          };
        });
      }

      breakPlaybackVideoSrc.value = capturedJson?.playback_video?.url;
      breakPlaybackVideoDuration.value = capturedJson?.playback_video?.duration;

      if (capturedJson.background) {
        if (capturedJson.background.background_image) {
          isBreakBackgroundAnImage.value = true;
          breakBackgroundImageSrc.value = capturedJson.background.background_image.url;
        } else if (capturedJson.background.background_color) {
          if (typeof capturedJson.background.background_color === 'object') {
            logInfo('Background Color Object placeholder');

            // // TODO: Integrate when given our first BG Color Object
            // breakBackgroundColor.value = capturedJson.background.background_color.color;
          } else {
            breakBackgroundColor.value = capturedJson.background.background_color;
          }
        } else {
          isBreakBackgroundFree.value = true;
          isBreakBackgroundAnImage.value = false;
        }
      }

      breakHeadlineCopy.value = capturedJson?.description;
      breakTaglineCopy.value = capturedJson?.call_to_action;

      breakProducts.value = (capturedJson.products || []).map((item) => {
        let capturedProductImagesList = [];

        if (item.product_image) {
          capturedProductImagesList.push({
            url: item.product_image.url,
            width: item.width,
            height: item.height,
          });
        } else {
          capturedProductImagesList = item.product_images;
        }

        return {
          productUuid: item.uuid,
          productSku: item.product_sku,
          productUrl: item.product_url,
          productImages: capturedProductImagesList.map((subItem) => {
            return {
              productImageSrc: subItem.url,
              // productImageWidth: subItem.width,
              // productImageHeight: subItem.height,
            };
          }),
          productName: item.product_title,
          productPosition: item.template_order,
        };
      });

      if (capturedJson.custom_attributes) {
        breakHeadlineColor.value = capturedJson.custom_attributes?.custom_colors?.description;
        breakTaglineColor.value = capturedJson.custom_attributes?.custom_colors?.call_to_action;
        breakScreenPlacement.value = capturedJson.custom_attributes?.screen_placement;

        if (capturedJson.custom_attributes.poll_options) {
          breakOptions.value = Object.keys(capturedJson.custom_attributes.poll_options).map(
            (key, index) => {
              return {
                optionEntry: capturedJson.custom_attributes.poll_options[key],
                optionLabel: key,
                optionIndex: index,
                optionPosition: index + 1,
              };
            },
          );
        }

        if (capturedJson.custom_attributes.trivia_options) {
          const triviaOptions = capturedJson.custom_attributes.trivia_options;
          breakTriviaTidbit.value = triviaOptions.trivia_tidbit ?? '';
          let correctOptionLabel = '';
          breakOptions.value = Object.keys(triviaOptions)
            .filter((key) => triviaOptions[key]?.text !== undefined)
            .map((key, index) => {
              const option = triviaOptions[key];
              if (option.is_correct && !correctOptionLabel) {
                correctOptionLabel = key;
              }
              return {
                optionEntry: option.text,
                optionLabel: key,
                optionIndex: index,
                optionPosition: index + 1,
              };
            });
          breakCorrectOption.value = correctOptionLabel;
        }

        if (capturedJson.custom_attributes.hotspots) {
          const productUuids = new Set(breakProducts.value.map((product) => product.productUuid));
          breakHotspots.value = filterValidHotspots(
            capturedJson.custom_attributes.hotspots,
            productUuids,
          ).map((hotspot) => {
            return {
              productUuid: hotspot.product_uuid,
              positionX: hotspot.position_x,
              positionY: hotspot.position_y,
              startsAt: hotspot.starts_at,
              duration: hotspot.duration,
              timingUnits: hotspot.timing_units,
            };
          });
        }

        if (capturedJson.custom_attributes.ai_generation) {
          breakAiTerm.value = capturedJson.custom_attributes.ai_generation?.ai_generate_term;
          breakAiType.value = capturedJson.custom_attributes.ai_generation?.ai_generate_type;

          if (capturedJson.custom_attributes.ai_generation.ai_generate_criteria) {
            breakAiCriteria.value =
              capturedJson.custom_attributes.ai_generation.ai_generate_criteria;
          } else {
            breakAiCriteria.value = 'top-rated';
          }
        }
      }

      breakCategories.value = capturedJson?.categories;

      if (breakType.value === 'tvOverlayStream') {
        if (breakTemplate.value === '11' || breakTemplate.value === 11) {
          // // TODO: x

          await dataAnalyzers
            .loadUrlJsonData('https://d20c5s7qgkgi77.cloudfront.net/')
            .then(async (capturedJsonX) => {
              console.log(' ');
              console.log('++++');
              console.log(`    capturedJsonX: ${JSON.stringify(capturedJsonX)}`);
              logInfo(`CAPTURED JSON X: `, capturedJsonX);
              console.log('++++');
              console.log(' ');
            })
            .catch(async (errorResponse) => {
              logInfo(`Error response caught: ${errorResponse}`);

              isUnableToPopulate.value = true;
              sendOverlayError(OVERLAY_ERROR.DATA_LOAD_FAILED, String(errorResponse));
              await fetch(`${catchErrorPrefix}${appInstance}/${errorResponse}`);
            });
        }
      }

      isBreakReady.value = true;
      isUnableToPopulate.value = false;

      if (breakType.value === 'tvOverlayStream') {
        if (breakTemplate.value === '11' || breakTemplate.value === 11) {
          // // The actual Viewer coordinates come from the following URL:
          // // https://d20c5s7qgkgi77.cloudfront.net/
          // // That endpoint CORS-blocks Local, so Local falls back to a fixed
          // // Santa Cruz, CA coordinate instead.  Live instances (Dev, Stage,
          // // & Prod) fetch the real Viewer location.
          let viewerLatitude = 36.98;
          let viewerLongitude = -122.01;

          if (appInstance !== 'localhost') {
            try {
              const viewerJson = await dataAnalyzers.loadUrlJsonData(
                'https://d20c5s7qgkgi77.cloudfront.net/',
              );
              viewerLatitude = viewerJson.viewer.latitude;
              viewerLongitude = viewerJson.viewer.longitude;
            } catch (errorResponse) {
              logError(`Viewer coordinates lookup failed, using fallback: ${errorResponse}`);
            }
          }

          breakAiRecommendationsStatus.value = 'pending';

          try {
            const localizedJson = await backendApis.getLocalizedRecommendationsData(
              viewerLatitude,
              viewerLongitude,
              breakAiType.value,
              breakAiTerm.value,
            );

            if (localizedJson) {
              breakAiRecommendations.value = localizedJson.recommendations;
              logDebug('getLocalizedRecommendationsData() SUCCESS', breakAiRecommendations.value);
            }
            breakAiRecommendationsStatus.value = 'success';
          } catch (errorResponse) {
            logError(`getLocalizedRecommendationsData() failed: ${errorResponse}`);
            breakAiRecommendationsStatus.value = 'error';
            // // A non-ok HTTP response carries a real status code (backendApis attaches it).
            // // A rejected fetch() with no status is a network-level failure — browsers don't
            // // expose *why* (CORS block vs. offline vs. DNS failure), so 'CORS' here is a
            // // best-guess label based on this app's known CORS-blocked-endpoint history, not a
            // // certainty.
            if (typeof errorResponse?.status === 'number') {
              breakAiRecommendationsErrorCode.value = String(errorResponse.status);
            } else if (errorResponse instanceof TypeError) {
              breakAiRecommendationsErrorCode.value = 'CORS';
            } else {
              breakAiRecommendationsErrorCode.value = null;
            }
          }
        }
      }

      // // Tracking && Comms to Parent Window to show App on screen
      isBreakInteracted.value = false;
      isVisible.value = false;
      // await waitForFonts();

      // // Confirmed content-overlay embed takes the real beacon path; otherwise fall back.
      let isConfirmedContentOverlay = false;
      try {
        isConfirmedContentOverlay = CommunicationUtil.isContentOverlay();
      } catch (error) {
        logError('App.vue — isContentOverlay() failed:', error);
      }

      if ((!isIframe.value || shouldSkipBeaconCalls.value) && !isConfirmedContentOverlay) {
        isVisible.value = true;
        logInfo(`Is visible manually set to: ${isVisible.value}`);
      } else {
        // // Only make beacon/parent calls if not in full mode or browser mode (browser iframe)
        isVisible.value = await CommunicationUtil.onRendered();
        // fetch(
        //   `${catchErrorPrefix}${appInstance}/appReachable?trackingInfo=${encodeURIComponent(JSON.stringify(trackingInfo))}`,
        // );
        if (
          !shouldSkipBeaconCalls.value &&
          isVisible.value &&
          trackingInfo.value?.trackingEvents?.length > 0
        ) {
          storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
            trackingInfo.value,
            'nasbUiShow',
            breakUuid.value,
          );
        }
      }

      // // NACX Metrics — overlay is now visible on screen (tvOverlayStream only; others use VAST beacons)
      if (breakType.value === 'tvOverlayStream') {
        if (isVisible.value) {
          sendOverlayShown();
        } else {
          sendOverlayError(OVERLAY_ERROR.IS_VISIBLE_REJECTED, 'TV parent declined to show overlay');
        }
      }

      fetch(`${catchErrorPrefix}${appInstance}/jsonLoaded?ib_uuid=${capturedJson.uuid}`);
    })
    .catch(async (errorResponse) => {
      logInfo(`Error response caught: ${errorResponse}`);

      isUnableToPopulate.value = true;
      sendOverlayError(OVERLAY_ERROR.DATA_LOAD_FAILED, String(errorResponse));
      await fetch(`${catchErrorPrefix}${appInstance}/${errorResponse}`);
    });
};

// // App lifecycle hooks

onBeforeMount(async () => {
  await initializer();
});

// // App watchers

watch(isBreakReady, (isBreakReadyNewVal) => {
  if (isBreakReadyNewVal) {
    logInfo('Break data has been properly received');

    // callDirectionGroup();
  }
});
</script>

<template>
  <SamsungShopping
    v-if="
      callDirectionGroup() === groupDirectory.basicOverlays.group ||
      callDirectionGroup() === groupDirectory.transparentOverlays.group ||
      callDirectionGroup() === groupDirectory.simplePolls.group ||
      callDirectionGroup() === groupDirectory.multiStageOverlays.group ||
      callDirectionGroup() === groupDirectory.heroPolls.group
    "
  />
  <FourthWall v-else-if="callDirectionGroup() === groupDirectory.fourthWall.group" />
  <TvPlusLinker v-else-if="callDirectionGroup() === groupDirectory.samsungTvPlus.group" />
  <Home v-else-if="callInabilityToPopulate()" />
</template>
